import { BaseElementWidget, FormWidget, SPI, ViewType, Widget, registerLayout } from '@kunlun/dependencies';
import SalesRatioControlConfigForm from './SalesRatioControlConfigForm.vue';
import { queryConfigById } from './api';

/**
 * 销售占比管控配置 —— 创建/编辑页全自定义 FORM 视图。
 * 仿照 PartCancelFormWidget：按 model + viewType + moduleName 注册（去掉 name），整页接管默认表单。
 */
@SPI.ClassFactory(
  BaseElementWidget.Token({
    viewType: [ViewType.Form, ViewType.Detail],
    widget: 'SalesRatioControlConfigFormWidget'
  })
)
export class SalesRatioControlConfigFormWidget extends FormWidget {
  public initialize(props) {
    super.initialize(props);
    this.setComponent(SalesRatioControlConfigForm);
    return this;
  }

  /** 是否只读（查看页） */
  @Widget.Reactive()
  public get readonly(): boolean {
    return (this as any).viewType === ViewType.Detail;
  }

  /** 运行时模块名，供子组件发起 GraphQL 查询/提交 */
  @Widget.Reactive()
  public get moduleName(): string {
    return this.model?.moduleName || 'deliAriesCorePlatform';
  }

  /** 编辑态初始记录（存在 id 视为编辑） */
  @Widget.Reactive()
  public get editRecord(): Record<string, any> | null {
    const record = this.activeRecords?.[0] as Record<string, any> | undefined;
    if (record && record.id) {
      return { ...record };
    }
    return null;
  }

  /** 保存完成后返回列表页 */
  @Widget.Method()
  public backToList(): void {
    history.back();
  }

  /**
   * Detail 视图时自己发起显式字段的 GraphQL 查询加载数据。
   * 框架对本自定义 form slot 无法收集字段元数据，其默认 fetchData 会生成
   * 裸 queryOne(query: {}) 触发 SubSelectionRequired，因此改用 queryConfigById。
   */
  protected async mountedProcess() {
    const self = this as any;
    if (self.readonly) {
      self.isDataSourceProvider = true;
      const id = self.activeRecords?.[0]?.id || this.resolveIdFromUrl();
      if (id) {
        const record = await queryConfigById(this.moduleName, `${id}`);
        const dataSource = record ? [record] : [];
        self.reloadDataSource(dataSource);
        self.reloadActiveRecords(dataSource);
      }
      self.repairViewMode?.();
    } else {
      super.mountedProcess();
    }
  }

  /** 从当前路由 URL 兜底解析记录 id（形如 ...;id=904612974054516807;...） */
  private resolveIdFromUrl(): string | null {
    try {
      const match = /[;&?]id=([^;&]+)/.exec(window.location.href);
      return match ? decodeURIComponent(match[1]) : null;
    } catch (e) {
      return null;
    }
  }
}

registerLayout(
  `<view type="FORM">
    <element widget="actionBar" slot="actionBar" slotSupport="action">
        <xslot name="actions" slotSupport="action" />
    </element>
    <element widget="SalesRatioControlConfigFormWidget" slot="form">
        <xslot name="fields" slotSupport="pack,field" />
    </element>
</view>`,
  {
    model: 'deli.aries.platform.DeliAriesPlatformSalesRatioControlConfig',
    viewType: ViewType.Form,
    moduleName: 'deliAriesCorePlatform'
  }
);

// Detail 布局：用显式字段声明替代 <xslot>，确保框架生成带字段子选择的 GraphQL 查询。
// 字段由自定义 widget 接管渲染，不会显示为标准字段控件。
registerLayout(
  `<view type="DETAIL">
    <element widget="actionBar" slot="actionBar" slotSupport="action">
        <xslot name="actions" slotSupport="action" />
    </element>
    <element widget="SalesRatioControlConfigFormWidget" slot="form">
        <element widget="field" name="id" />
        <element widget="field" name="controlLevel" />
        <element widget="field" name="channelType" />
        <element widget="field" name="provincialBranchCode" />
        <element widget="field" name="provincialBranchName" />
        <element widget="field" name="salesOfficeCode" />
        <element widget="field" name="salesOfficeName" />
        <element widget="field" name="customerCode" />
        <element widget="field" name="customerErpCode" />
        <element widget="field" name="customerName" />
        <element widget="field" name="customerDistributor" />
        <element widget="field" name="promoSalesRatio" />
        <element widget="field" name="customerMarginRatio" />
        <element widget="field" name="provinceMarginRatio" />
        <element widget="field" name="oldProductMarginRate" />
        <element widget="field" name="newProductMarginRate" />
    </element>
</view>`,
  {
    model: 'deli.aries.platform.DeliAriesPlatformSalesRatioControlConfig',
    viewType: ViewType.Detail,
    moduleName: 'deliAriesCorePlatform'
  }
);
